"use client";

import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/Button";
import { PRIVACY_URL } from "@/lib/legal/terms";
import { setMarketingConsent, type MarketingConsent } from "@/lib/consent/marketing-consent";

/**
 * The cookie question, asked on the sign-up page and nowhere else in the app.
 *
 * Paid Meta clicks land straight on app.mucka.ai/signup without ever seeing the
 * website's banner, so this page asks the same question in the same voice. The
 * answer goes in the shared `.mucka.ai` cookie, so somebody who already
 * answered on mucka.ai (once the website writes it too) is not asked twice.
 *
 * A centred card over the dimmed form, not a bottom banner: a banner was easy
 * to scroll past, and an ignored banner is a no. It is a forced CHOICE, never
 * a cookie wall — either answer lets them straight in.
 *
 * UK PECR: the Pixel needs a yes BEFORE it loads, and "Essential only" has to
 * be as easy as "Yeah, go on" — same size, side by side, one tap each. A line
 * in the Terms of Use is not consent. "Cookie settings" in the page footer
 * reopens this, which is the easy way to change your mind PECR also asks for.
 */
export default function SignupCookieConsent({ open, onAnswer }: { open: boolean; onAnswer: () => void }) {
  const cardRef = useRef<HTMLDivElement>(null);
  // Focus the card so a keyboard or screen reader starts on the question, not the form behind it.
  useEffect(() => {
    if (open) cardRef.current?.focus();
  }, [open]);

  if (!open) return null;

  function answer(value: MarketingConsent) {
    setMarketingConsent(value);
    onAnswer();
  }

  return (
    <div className="signup-consent">
      <div
        ref={cardRef}
        className="signup-consent-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="signup-consent-title"
        tabIndex={-1}
      >
        <h2 id="signup-consent-title" className="t-heading-md signup-consent-title">
          Cookies, quickly
        </h2>
        <p className="t-body-md signup-consent-text">
          We use essential cookies to make sign-up work. We&apos;d also like to use Meta&apos;s cookies to see
          which of our ads brought you here. Sound alright? More in our{" "}
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          .
        </p>
        <div className="signup-consent-actions">
          <Button variant="secondary" size="md" type="button" onClick={() => answer("denied")}>
            Essential only
          </Button>
          <Button variant="primary" size="md" type="button" onClick={() => answer("granted")}>
            Yeah, go on
          </Button>
        </div>
      </div>
    </div>
  );
}
